import 'whatwg-fetch';
import Vue from 'vue';
import router from '../router';

export const DEVELOPMENT = process.env.NODE_ENV && process.env.NODE_ENV === "development";

//MP_BASE [localhost,maklerportal,piadbtmaklerportal,...] configures the mode how URLs must be adjusted
const MP_BASE = process.env.VUE_APP_MP_BASE ?? 'maklerportal';
const LOGIN_URL = process.env.VUE_APP_LOGIN_URL ?? '/maklerportal/Login.aspx/Login';
export const API_URL = process.env.VUE_APP_API_URL ?? '/maklerportalneu/api/';
export const API_URL_SERVER = process.env.VUE_APP_API_URL_SERVER ?? '';
export const API_URL_LEGACY = process.env.VUE_APP_API_URL_LEGACY ?? '/maklerportal';
export const API_CONTENT = process.env.VUE_APP_API_CONTENT ?? 'https://test.dmb-rechtsschutz.de/wp-json/wp/v2/pages?slug=';

/*const MP_BASE = "piadbtmaklerportal";
const LOGIN_URL = DEVELOPMENT ? 'http://localhost:54203/Login.aspx/Login' : '/piadbtmaklerportal/Login.aspx/Login';
// Visual Studio local Development backend port 54575 used by cav.exe new port 54555
export const API_URL = DEVELOPMENT ? 'http://localhost:54555/api/' : '/piadbtmaklerportalneu/api/';
export const API_URL_SERVER = DEVELOPMENT ? 'http://localhost:54555' : '';
export const API_URL_LEGACY = DEVELOPMENT ? 'http://localhost:54203' : '/piadbtmaklerportal';
*/
export const CONNECTION_ERROR_MESSAGE = 'Server derzeit nicht verfügbar';
export const CONNECTION_ERROR_TEXT = 'Bitte warten Sie einen Moment und versuchen Sie es erneut.';

const DEFAULT_OPTIONS = {
  method: 'GET',
  credentials: DEVELOPMENT ? 'include' : 'same-origin',
  headers: { 'Content-Type': 'application/json' },
};

async function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    let error = new Error(getErrorMessage(response.status));
    error.status = response.status;
    if (response.status === 401) {
      // For 401, defer redirect to next tick to avoid router not being ready and causing in promise error
      Vue.nextTick(() => {
        router.push('/logout').catch(() => {
          // Suppress navigation error (e.g., if route doesn't exist or guard rejects)
        });
      });
      throw error;
    }
    return response.json()
      .catch( // ignoring body parsing errors
        () => "")
      .then(jsonBody => {
        error.body = jsonBody;
        throw error;
      });
  }
}

function getErrorMessage(status) {
  if (!status)
    return 'Netzwerkfehler';
  if (status === 401)
    return 'Login erforderlich';
  if (status === 403)
    return 'Aktion nicht erlaubt';
  if (status === 422)
    return 'Ungültige Eingaben';
  if (status === 404)
    return 'Nicht gefunden';
  return 'Leider ist ein Server-Fehler aufgetreten. Bitte laden Sie die Seite neu.';
}

async function parseJSON(response) {
  return response.status === 204 ? null : response.json();
}

async function doRequest(url, method, body) {
  let data = Object.assign({}, DEFAULT_OPTIONS);
  if (method) data.method = method;
  if (body) data.body = JSON.stringify(body);
  return fetch(url, data)
    .catch(() => {
      let err = new Error(getErrorMessage());
      err.status = 504;
      throw err;
    })
    .then(checkStatus)
    .then(parseJSON);
}

async function combineResults(results, resultsFieldName) {
  return results.reduce((total, result) => {
    if (result.error) {
      total.errors.push(result.error);
    } else {
      total[resultsFieldName] = total[resultsFieldName].concat(result);
    }
    return total;
  }, { [resultsFieldName]: [], errors: [] });
}

export default {

  /**
   * @param {Object} user - { username: String, password: String )
   * @return {Promise<Object>} - Object User: {username: String, vmnr: String, maklertyp: String, tarifsatz: String}
   */
  async login(user) {
    return doRequest(LOGIN_URL, 'POST', user)
      .then(user => user.d);
  },

  adjustUrl(documentPath) {
    if(documentPath.indexOf('/api/') >= 0){
      return this.adjustApiUrl(documentPath);
    } else {
      return this.adjustLegacyUrl(documentPath);
    }
  },

  adjustLegacyUrl(documentPath) {
    if(documentPath.indexOf('maklerportal/') >= 0){
      if(DEVELOPMENT){
        return API_URL_LEGACY + documentPath.substring( documentPath.indexOf('maklerportal/') + 12);
      } else {
        return documentPath.replace('/maklerportal/', '/' + MP_BASE + '/');
      }
    }
      return documentPath;
  },

  adjustApiUrl(documentPath) {
    if(documentPath.indexOf('maklerportal/') >= 0){
      if(DEVELOPMENT){
        return API_URL_SERVER + documentPath.substring( documentPath.indexOf('maklerportal/') + 12);
      } else {
        return documentPath.replace('/maklerportal', '/' + MP_BASE );
      }
    } else if (DEVELOPMENT && documentPath.indexOf('http') !== 0) {
      return API_URL_SERVER + documentPath;
    }
    return documentPath;
  },

  /**
   * @return {Promise<Object>} - Object: { message: String }
   */
  async logout() {
    return doRequest(`${API_URL}user/logout`);
  },

  /**
   * @param userId get whoami information for this userId. If undefined, send request without userId,
   *               resulting in info for current user
   * @return {Promise<Object>} - Object User: { username: String, vmnr: String, maklertyp: String, tarifsatz: String, metadata: Object}
   */
  async getWhoAmI(userId) {
    if (!userId) {
      return doRequest(`${API_URL}user/whoami`);
    } else {
      return doRequest(`${API_URL}user/whoami/${userId}`);
    }
  },
  /**
   * @param frontendVersion 
   *               retrieve info on current version from server
   * @return {Promise<Object>} - Object: {  ok: bool, requestversion: String, compareversion: String, status: String }
   */
  async getServerVersion(frontendVersion) {
    if (frontendVersion) {
      return await doRequest(`${API_URL}version/${frontendVersion}/${+new Date()}`);
    } else {
      return { ok: true, requestversion: frontendVersion };
    }
  },
  /**
   * @return {Promise<Object>} - Object: { anrede: String, titel: String, vorname: String, nachname: String, ...}
   */
  async getProfile() {
    return doRequest(`${API_URL}user/profile`);
  },

  /**
   * @param {Object} data - Object: { anrede: String, titel: String, vorname: String, nachname: String, ...}
   * @return {Promise<Object>} - Object: { anrede: String, titel: String, vorname: String, nachname: String, ...}
   */
  async updateProfile(data) {
    return doRequest(`${API_URL}user/updateprofile`, 'POST', data);
  },

  /**
   * @return {Promise<Array>} - Courtage[] mit Object Courtage: { url: String, courtagedate: String, reationdate: String }
   */
  async getCourtagen() {
    return doRequest(`${API_URL}user/courtages`);
  },

  /**
   * @param {Object} data - { newpassword: String, oldpassword: String )
   * @return {Promise<Object>} - who knows
   */
  async changePassword(data) {
    return doRequest(`${API_URL}user/profile/changepassword`, 'POST', data);
  },


  /**
   * @param {Object} data - { Benutzername: String, Email: String )
   * @return {Promise<Boolean>} - Falsche/r Benutzername / Email => false
   */
  async resetPassword(data) {
    return doRequest(`${API_URL}resetpassword`, 'POST', data);
  },

  /**
   * @param {String} [vmnr] - optional: brokernumber
   * @return {Promise<Array>} - Vertrag[] mit Object Vertrag: { Vertragsnummer: String, Beginn: String, Ende: String, ...}
   */
  async getBestand(vmnr) {
    return doRequest(`${API_URL}user/contracts/${vmnr ? vmnr : ''}`);
  },

  /**
   * @param {String} vertragsnummer
   * @param {String} [vmnr] - optional: brokernumber
   * @return {Promise<Object>} - Object Vertrag: { Vertragsnummer: String, Beginn: String, Ende: String, ...}
   */
  async getVertrag(vertragsnummer, vmnr) {
    return doRequest(`${API_URL}user/contract/${vertragsnummer}/${ vmnr ? vmnr : '' }`);
  },

  /**
   * @return {Promise<{angebote: Array, errors: Array} >} - angebote contains all succesfully retrieved angebote;
   * error contains all errors that occurred. A combination of error and a list of angebote from another source is a
   * possible scenario
   */
  async getAngebote() {
    const modernAngebote = doRequest(`${API_URL}offers`).catch(error => ({ error }));
    const legacyAngebote = doRequest(`${API_URL}offers/legacy`).catch(error => ({ error }));
    return Promise.all([modernAngebote, legacyAngebote])
      .then(results => combineResults(results, "angebote"));
  },

  /**
   * @param {String} angebotsnummer
   * @return {Promise<Object>} - Object Angebot: { firstname: String, lastname: String, ... }
   */
  async getAngebot(angebotsnummer) {
    return doRequest(`${API_URL}offers/byoffernumber/${angebotsnummer}`);
  },

  /**
   * @param {String} [vmnr] - optional: brokernumber
   * @return {Promise<Array>} - Schaden[] mit Object Schaden: { damage: Object, contract: Object }
   */
  async getSchaedenForUser(vmnr) {
    return doRequest(`${API_URL}user/damages/${ vmnr ? vmnr : '' }`);
  },

  /**
   * @param {String} vertragsnummer
   * @param {String} [vmnr] - optional: brokernumber
   * @return {Promise<Array>} - Schaden[] mit Object Schaden: { Vertragsnummer: String, Schadennummer: String, Ereignisdatum: String,
   *                            Meldedatum: String, Deckungszusage: String, erledigt: String, Kosten: String, VertragsTyp: String }
   */
  async getSchaedenForVertrag(vertragsnummer, vmnr) {
    return doRequest(`${API_URL}user/contract/${vertragsnummer}/damages/${ vmnr ? vmnr : '' }`);
  },

  /**
   * @returns {Promise<*>} 200 if deleted
   */
  async deleteAngebot(id) {
    return doRequest(`${API_URL}offers/delete/${id}`);
  },

  /**
   * @returns {Promise<*>} 200 if deleted
   */
  async deleteLegacyAngebot(angebotsnummer) {
    return doRequest(`${API_URL}offers/legacy/delete/${angebotsnummer}`);
  },

  /**
   * @param {Object} data
   * @param {String} type - 'Angebot' oder 'Kurzangebot'
   * @return {Promise<Object>} - Object { url: String }
   */
  async createAngebot(data, type) {
    return doRequest(`${API_URL}offers/save/${type === 'Kurzangebot' ? 'short' : 'long'}`, 'POST', data);
  },

  /**
   * @param {Object} data
   * @param {String} type - 'Angebot' oder 'Kurzangebot'
   * @return {Promise<Object>} - Object { url: String }
   */
  async createGewerbeAngebot(data, type) {
    return doRequest(`${API_URL}gewerbe/${type === 'Kurzangebot' ? 'short' : 'long'}offer/save`, 'POST', data);
  },

  /**
   * @param {Object} data
   * @return {Promise<Object>} - Object { }
   */
  async submitAntrag(data) {
    return doRequest(`${API_URL}application/commit`, 'POST', data);
  },

  /**
   * @param {Object} data
   * @return {Promise<Object>} - Object { }
   */
  async submitGewerbeAntrag(data) {
    return doRequest(`${API_URL}application/commit/gewerbe`, 'POST', data);
  },

  /**
   * @return {Promise<{modernResult: Array, legacyResult: Array, errors: Array} >}
   */
  async getAntraege() {

    let modernResult, legacyResult;

    try {
      modernResult = await doRequest(`${API_URL}applications`);
    } catch(error) {
      modernResult = { error };
    }

    try {
      legacyResult = await doRequest(`${API_URL}applications/legacy`);
    } catch(error) {
      legacyResult = { error };
    }
    const result = {
      errors: [],
      modernResult: [],
      legacyResult: []
    };
    if (modernResult.error) {
      result.errors.push(modernResult.error);
    } else {
      result.modernResult = modernResult;
    }
    if (legacyResult.error) {
      result.errors.push(legacyResult.error);
    } else {
      result.legacyResult = legacyResult;
    }
    return result;
  },

  /**
   * Get information for a single Antrag.
   */
  async getAntrag(vorgangsnummer) {
    return doRequest(`${API_URL}applications/${vorgangsnummer}`);
  },

  /**
   * @param antrag format like response of getAntrag, plus "processinginfo" field
   */
  async resendAntrag(antrag) {
    return doRequest(`${API_URL}applications/sendmail`, 'POST', antrag);
  },

  async getStaticContent(path) {
    if (/VertriebRechtAktuell|StartseiteAuszeichnungen|StartseiteNews|AboutAuszeichnungenUnternehmen|AboutAuszeichnungenUnternehmenBadges|AboutAuszeichnungenProdukte|AboutAuszeichnungenProdukteBadges|AboutPresse|AboutUnternehmen|AboutUnternehmenDownloads/i.test(path) ) {
      const slug = path.toLowerCase().replace(/^aboutauszeichnungen(?!-)/, 'aboutauszeichnungen-').replace(/aboutunternehmendownloads/, 'aboutunternehmen-downloads');
      return doRequest(`${API_CONTENT}${slug}`);
    } else {
      // TYPO3 content
      console.warn(`Requesting ${path} static content from TYPO3 is deprecated use API_CONTENT endpoint instead.`);
      return doRequest(`${API_URL}content/${path.toLowerCase()}`);
    }
  },

  async checkUserName(username) {
    return doRequest(`${API_URL}registration/checkusername`, 'POST', username);
  },
  /**
   * check if vmnr can be activated
   * @param {string} vmnr 
   * @returns 
   */
  async checkVermittlernummer(vmnr) {
    return doRequest(`${API_URL}checkvermittlernummer`, 'POST', vmnr);
  },
  /**
   * check if vmnr can be activated
   * @param {string} vmnr 
   * @returns 
   */
   async initVermittlernummer(vmnr) {
    return doRequest(`${API_URL}makleractivation/${vmnr}`, 'POST', vmnr);
  },

  /**
   * @param {Object} user - Object: { loginname: String, vorname: String, nachname: String, ...}
   * @return {Promise<Object>} - Object: { success: Boolean }
   */
  async registerNewUser(user) {
    return doRequest(`${API_URL}register`, 'POST', user);
  },

  /**
   * Request list of places (cities) for given plz
   * @param {string} plz 5 digit number, german plz
   */
  async getCitiesFromPlz(plz) {
    return doRequest(`${API_URL}places/${plz}`, 'GET');
  }

};

export const praemienService = {

  /**
   * @param tarifId id of target tarif. If undefined, request without id, resulting in default tarif for current user
   * @return {Promise<Object>} - Object Pricematrix
   */
  async getPrice(tarifId, tarifVersion) {
    if (!tarifId && !tarifVersion) {
      return doRequest(`${API_URL}price`);
    } else if (tarifId && !tarifVersion) {
      return doRequest(`${API_URL}price/${tarifId}`);
    } else if (!tarifId && tarifVersion) {
      return doRequest(`${API_URL}price/basis/${tarifVersion}`);
    } else {
      return doRequest(`${API_URL}price/${tarifId}/${tarifVersion}`);
    }
  },

  /**
   * @param tarifVersion version of tariff (e.g., '2024', '2026')
   * @return {Promise<Object>} - Object Pricematrix
   */
  async getGewerbePrice(tarifVersion) {
    if (!tarifVersion) {
      return doRequest(`${API_URL}gewerbe/price`);
    } else {
      return doRequest(`${API_URL}gewerbe/price/${tarifVersion}`);
    }
  }

};

